import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { assertUnreachable } from '../../../../../util/assertUnreachable';

export const questionCountOptions = [5, 10, 15];

interface GenerateQuestionState {
  includeExplanation: boolean;
  questionCount: typeof questionCountOptions[number];
  [QuestionTypes.MultipleChoice]: boolean;
  [QuestionTypes.TrueFalse]: boolean;
  [QuestionTypes.FreeResponse]: boolean;
  fileKey: string | null;
  fileMetadata: { type: string; size: number } | null;
}

export type GenerateQuestionAction =
  | { type: 'ResetDialog' }
  | {
      type: 'QuestionTypeChange';
      payload: { questionType: string; value: boolean };
    }
  | { type: 'NumberQuestionChange'; payload: { value: number } }
  | {
      type: 'IncludeExplanationChange';
      payload: { includeExplanations: boolean };
    }
  | {
      type: 'AddFileKey';
      payload: {
        fileKey: string;
        fileMetadata: { type: string; size: number };
      };
    }
  | { type: 'RemoveFileKey' };

const getInitialState = (): GenerateQuestionState => ({
  includeExplanation: true,
  questionCount: 10,
  [QuestionTypes.MultipleChoice]: true,
  [QuestionTypes.TrueFalse]: true,
  [QuestionTypes.FreeResponse]: true,
  fileKey: null,
  fileMetadata: null,
});

function reducer(state: GenerateQuestionState, action: GenerateQuestionAction) {
  switch (action.type) {
    case 'QuestionTypeChange': {
      const { questionType, value } = action.payload;
      return { ...state, [questionType]: value };
    }
    case 'IncludeExplanationChange': {
      const { includeExplanations } = action.payload;
      return { ...state, includeExplanation: includeExplanations };
    }
    case 'NumberQuestionChange': {
      const { value } = action.payload;
      return { ...state, questionCount: value };
    }
    case 'AddFileKey': {
      const { fileKey, fileMetadata } = action.payload;
      return { ...state, fileKey: fileKey, fileMetadata: fileMetadata };
    }
    case 'RemoveFileKey': {
      return { ...state, fileKey: null, fileMetadata: null };
    }
    case 'ResetDialog': {
      return getInitialState();
    }
    default:
      assertUnreachable(action);
  }
}

const baseGenerateQuestionAtom = atomWithStorage<GenerateQuestionState>(
  'generateQuestion',
  getInitialState()
);
export const generateQuestionAtom = atom(
  (get) => get(baseGenerateQuestionAtom),
  (get, set, action: GenerateQuestionAction) => {
    set(
      baseGenerateQuestionAtom,
      reducer(get(baseGenerateQuestionAtom), action)
    );
  }
);

export function checkIsQuestionTypeEmpty(
  multipleChoice: boolean,
  trueFalse: boolean,
  shortAnswers: boolean
) {
  return !multipleChoice && !trueFalse && !shortAnswers;
}
